import { useMutation, useQueryClient } from '@tanstack/react-query'

import dialogStyles from '@/pages/app/Enterprises/components/styles.module.scss'

import { isApiError } from '@/api'
import { toggleDomainDeleted } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { useToast } from '@/components/Toast/ToastContext'

interface ToggleDomainDeletedDialogProps {
  isOpen: boolean
  enterpriseId: string
  domainId: string
  domain: string
  deleted: boolean
  onSuccess: (workos_synced: boolean) => void
  onCancel: () => void
}

const ToggleDomainDeletedDialog = ({
  isOpen,
  enterpriseId,
  domainId,
  domain,
  deleted,
  onSuccess,
  onCancel,
}: ToggleDomainDeletedDialogProps) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()

  const mutation = useMutation({
    mutationFn: () => toggleDomainDeleted(enterpriseId, domainId, deleted),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['enterpriseDetails', enterpriseId] })
      updateToast(`Domain ${deleted ? 'deleted' : 'restored'}`, 'success')
      onSuccess(response.data.workos_synced ?? true)
    },
    onError: (err) => {
      if (isApiError(err)) {
        updateToast(err.detail || 'Failed to update domain', 'error')
      } else {
        updateToast('Failed to update domain', 'error')
      }
    },
  })

  return (
    <DialogWrapper
      isOpen={isOpen}
      title={deleted ? 'Delete domain' : 'Restore domain'}
      subtitle={
        deleted
          ? `Deleting "${domain}" will disable auto-invite for new signups, remove it from the organization's settings page, and remove it from WorkOS SSO.`
          : `Restoring "${domain}" will enable auto-invite for new signups, show it on the organization's settings page, and sync it to WorkOS for SSO.`
      }
      close={onCancel}
    >
      <div className={dialogStyles.dialogContent}>
        <div className={dialogStyles.dialogActions}>
          <Button title="Cancel" onClick={onCancel} type="light" />
          <Button
            title={deleted ? 'Delete Domain' : 'Restore Domain'}
            onClick={() => mutation.mutate()}
            type="dark"
            isLoading={mutation.isPending}
            isDisabled={mutation.isPending}
          />
        </div>
      </div>
    </DialogWrapper>
  )
}

export default ToggleDomainDeletedDialog
